import React, { useState, useMemo } from "react";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import HomeIcon from "@mui/icons-material/Home";
import BusinessIcon from "@mui/icons-material/Business";
import ScienceIcon from "@mui/icons-material/Science";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonIcon from "@mui/icons-material/Person";
import GrassIcon from "@mui/icons-material/Grass";
import MenuIcon from "@mui/icons-material/Menu";
import CompostIcon from "@mui/icons-material/Compost";
import EditIcon from "@mui/icons-material/Edit";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import LanguageIcon from "@mui/icons-material/Language";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AssignmentIcon from "@mui/icons-material/Assignment";
import StarIcon from "@mui/icons-material/Star";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import SettingsIcon from "@mui/icons-material/Settings";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useUser } from "../../Context/UserContext";
import { useTranslation } from "react-i18next";
import "./Header.css";

// --- Configuration ---
const OFFICER_ROLES = ["superadmin", "department_admin"];
const OFFICER_ROLES2 = [...OFFICER_ROLES, "district_admin", "block_admin"];

const allNavItems = [
  { label: "Home", path: "/", icon: <HomeIcon fontSize="small" /> },
  {
    label: "Production",
    path: "/production",
    icon: <BusinessIcon fontSize="small" />,
  },
  {
    label: "Species",
    path: "/species",
    icon: <ScienceIcon fontSize="small" />,
  },
  {
    label: "Species Allocations",
    path: "/allocations",
    icon: <GrassIcon fontSize="small" />,
  },
  {
    label: "Inspection",
    path: "/inspection",
    icon: <FactCheckIcon fontSize="small" />,
  },
  {
    label: "View Status",
    path: "/status",
    icon: <GrassIcon fontSize="small" />,
  },
  { label: "Scheme", path: "/scheme", icon: <BusinessIcon fontSize="small" /> },
  {
    label: "Generate Bill",
    path: "/bill",
    icon: <GrassIcon fontSize="small" />,
  },
  {
    label: "Report",
    path: "/Districtreport",
    icon: <BusinessIcon fontSize="small" />,
  },
  {
    label: "Request saplings",
    path: "/request-sapling",
    icon: <CompostIcon fontSize="small" />,
  },
  { label: "Target", path: "/target", icon: <CompostIcon fontSize="small" /> },
  {
    label: "Edit Info",
    path: "/edit-info",
    icon: <EditIcon fontSize="small" />,
  },
  {
    label: "Register",
    path: "/register",
    icon: <PersonAddIcon fontSize="small" />,
  },
  {
    label: "Assign Schemes",
    path: "/ProductionCenterScheme",
    icon: <AssignmentIcon fontSize="small" />,
  },
];

const DrawerWidth = 280;

const Header = ({ onLogout }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.between("md", "lg"));

  const location = useLocation();
  const navigate = useNavigate();
  const { authData } = useUser();
  const { i18n } = useTranslation();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [desktopLangAnchor, setDesktopLangAnchor] = useState(null);
  const [showDrawerLangDropdown, setShowDrawerLangDropdown] = useState(false);
  const [expandedDropdown, setExpandedDropdown] = useState(null); // Added for Drawer Dropdown toggle

  // --- STATE FOR MASTER DROPDOWN (Desktop) ---
  const [masterMenuAnchor, setMasterMenuAnchor] = useState(null);
  const isMasterMenuOpen = Boolean(masterMenuAnchor);

  const handleMasterMenuClick = (event) => {
    setMasterMenuAnchor(event.currentTarget);
  };
  const handleMasterMenuClose = () => {
    setMasterMenuAnchor(null);
  };

  const getSavedLangLabel = () => {
    const savedKey = localStorage.getItem("appLanguage");
    return savedKey === "ta" ? "தமிழ்" : "English";
  };
  const [currentLang, setCurrentLang] = useState(getSavedLangLabel());

  const isAuthenticated = !!authData?.role;

  const handleDesktopLangOpen = (event) =>
    setDesktopLangAnchor(event.currentTarget);
  const handleDesktopLangClose = () => setDesktopLangAnchor(null);

  const handleLanguageChange = (lang, key) => {
    setCurrentLang(lang);
    i18n.changeLanguage(key);
    localStorage.setItem("appLanguage", key);
    setShowDrawerLangDropdown(false);
    handleDesktopLangClose();
  };

  // Helper to check if any path in an array is active
  const isPathActive = (paths) =>
    paths.some((path) => location.pathname === path);
  console.log("auth data", authData);

  const navItems = useMemo(() => {
    const role = authData?.role;
    if (!role) return [allNavItems[0]];

    let items = [];

    // --- FIELD INSPECTOR ---
    if (role === "field_inspector") {
      items = [];
    }
    // --- PRODUCTION CENTER ---
    else if (role === "productioncenter") {
      if (!authData?.production_center_id) return [];
      items = allNavItems.filter((item) =>
        ["Home", "Species", "Species Allocations"].includes(item.label),
      );
    }
    // --- DEPARTMENT ADMIN & SUPER ADMIN (OFFICER_ROLES) ---
    else if (OFFICER_ROLES.includes(role)) {
      // Base items: Home, Target, Production
      const baseItems = allNavItems.filter((item) =>
        ["Home", "Target", "Production"].includes(item.label),
      );

      // Master Dropdown Items: Edit Info, Scheme, Inspection, Assign Schemes
      const masterSubItems = allNavItems.filter((item) =>
        ["Edit Info", "Scheme", "Inspection", "Assign Schemes"].includes(
          item.label,
        ),
      );

      // Construct the structure properly to avoid overwriting
      items = [...baseItems];

      // Dashboard Control for Super Admin ONLY (Pushed before Master dropdown)
      if (role === "superadmin" || role === "Super Admin") {
        items.push({
          label: "Dashboard Control",
          path: "/super-control",
          icon: <StarIcon fontSize="small" />,
        });
      }

      // Append Master Dropdown at the end
      items.push({
        label: "Master",
        icon: <SettingsIcon fontSize="small" />,
        children: masterSubItems,
      });
    }
    // --- DISTRICT/BLOCK ADMIN (OFFICER_ROLES2) ---
    else if (OFFICER_ROLES2.includes(role)) {
      items = allNavItems.filter((item) =>
        [
          "Home",
          "Target",
          "Production",
          "Inspection",
          "Assign Schemes",
        ].includes(item.label),
      );
    }
    // --- OTHERS ---
    else {
      switch (role) {
        case "admin":
          items = allNavItems.filter((item) =>
            [
              "Home",
              "Target",
              "Production",
              "Register",
              "Inspection",
              "Assign Schemes",
            ].includes(item.label),
          );
          break;
        case "farmer":
          items = allNavItems.filter((item) =>
            ["Home", "View Status", "Request saplings"].includes(item.label),
          );
          break;
        default:
          items = [allNavItems[0]];
      }
    }

    return items;
  }, [authData?.role, authData?.production_center_id]);

  const handleNavigation = (path) => {
    navigate(path);
    setDrawerOpen(false);
  };

  const getInitials = (name) => (!name ? "U" : name.charAt(0).toUpperCase());

  const getRoleLabel = (role) => {
    const map = {
      superadmin: "Super Admin",
      department_admin: "Dept Admin",
      district_admin: "District Admin",
      block_admin: "Block Admin",
      admin: "Admin",
      field_inspector: "Field Inspector",
      productioncenter: "Production Center",
      farmer: "Farmer",
    };
    return map[role] || role || "User";
  };

  // --- DRAWER CONTENT ---
  const DrawerContent = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        bgcolor: "#4a8234",
        position: "relative",
      }}
    >
      {/* Profile Header */}
      <Box
        sx={{
          p: 2.5,
          bgcolor: "#3d6e2b",
          borderBottom: "1px solid rgba(255,255,255,0.1)",
          flexShrink: 0,
          position: "relative",
          zIndex: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar
              sx={{
                width: 48,
                height: 48,
                bgcolor: "#ffc022",
                color: "#333",
                fontWeight: 700,
                fontSize: "1.1rem",
                border: "2px solid rgba(255,255,255,0.3)",
              }}
            >
              {getInitials(authData?.username)}
            </Avatar>
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "0.9rem",
                  lineHeight: 1.3,
                  maxWidth: "140px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {authData?.username || "User"}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "rgba(255,255,255,0.7)",
                  fontSize: "0.7rem",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  mt: 0.2,
                }}
              >
                <PersonIcon sx={{ fontSize: 12 }} />
                {getRoleLabel(authData?.role)}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={() => {
              setDrawerOpen(false);
              setShowDrawerLangDropdown(false);
            }}
            size="small"
            sx={{ color: "#fff" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Navigation List */}
      <List
        sx={{
          flex: 1,
          px: 1.5,
          py: 1.5,
          overflowY: "auto",
          position: "relative",
          zIndex: 2,
        }}
      >
        {navItems.map((item, index) => {
          // HANDLE DROPDOWN ITEMS (Master)
          if (item.children) {
            const isDropdownOpen =
              expandedDropdown === item.label ||
              isPathActive(item.children.map((c) => c.path));

            return (
              <Box key={`dropdown-${index}`}>
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => {
                      setExpandedDropdown((prev) =>
                        prev === item.label ? null : item.label,
                      );
                    }}
                    sx={{
                      borderRadius: 2,
                      py: 1.1,
                      px: 1.5,
                      bgcolor: isDropdownOpen
                        ? "rgba(255,193,7,0.2)"
                        : "transparent",
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 36,
                        color: isDropdownOpen ? "#ffc022" : "#fff",
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontSize: "0.85rem",
                        fontWeight: isDropdownOpen ? 700 : 500,
                        color: isDropdownOpen ? "#ffc022" : "#fff",
                      }}
                    />
                    <ExpandMoreIcon
                      sx={{
                        fontSize: 16,
                        color: "#fff",
                        transform: isDropdownOpen
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                        transition: "0.2s",
                      }}
                    />
                  </ListItemButton>
                </ListItem>

                {/* Sub Items */}
                <Box sx={{ pl: 2, display: isDropdownOpen ? "block" : "none" }}>
                  {item.children.map((child) => {
                    const isChildActive = location.pathname === child.path;
                    return (
                      <ListItem
                        key={child.path}
                        disablePadding
                        sx={{ mb: 0.5 }}
                      >
                        <ListItemButton
                          onClick={() => handleNavigation(child.path)}
                          sx={{
                            borderRadius: 2,
                            py: 0.8,
                            px: 1.5,
                            bgcolor: isChildActive
                              ? "rgba(255,193,7,0.15)"
                              : "transparent",
                          }}
                        >
                          <ListItemText
                            primary={child.label}
                            primaryTypographyProps={{
                              fontSize: "0.8rem",
                              color: isChildActive
                                ? "#ffc022"
                                : "rgba(255,255,255,0.9)",
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </Box>
              </Box>
            );
          }

          // HANDLE STANDARD ITEMS
          const isActive = location.pathname === item.path;
          return (
            <ListItem key={item.path || index} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => handleNavigation(item.path)}
                sx={{
                  borderRadius: 2,
                  py: 1.1,
                  px: 1.5,
                  bgcolor: isActive ? "rgba(255,193,7,0.2)" : "transparent",
                }}
              >
                <ListItemIcon
                  sx={{ minWidth: 36, color: isActive ? "#ffc022" : "#fff" }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: "0.85rem",
                    fontWeight: isActive ? 700 : 500,
                    color: isActive ? "#ffc022" : "#fff",
                  }}
                />
                {isActive && (
                  <ChevronRightIcon sx={{ fontSize: 16, color: "#ffc022" }} />
                )}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      {/* Drawer Footer */}
      <Box
        sx={{
          px: 1.5,
          pb: 2,
          borderTop: "1px solid rgba(255,255,255,0.1)",
          pt: 1.5,
          flexShrink: 0,
          bgcolor: "#4a8234",
        }}
      >
        {!isAuthenticated && (
          <Box sx={{ position: "relative", mb: 1 }}>
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => setShowDrawerLangDropdown((prev) => !prev)}
                sx={{
                  borderRadius: 2,
                  py: 1,
                  px: 1.5,
                  bgcolor: showDrawerLangDropdown
                    ? "rgba(255,255,255,0.15)"
                    : "transparent",
                }}
              >
                <ListItemIcon sx={{ minWidth: 36, color: "#fff" }}>
                  <LanguageIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={currentLang}
                  primaryTypographyProps={{
                    fontSize: "0.85rem",
                    color: "#fff",
                  }}
                />
                <ExpandMoreIcon
                  sx={{
                    fontSize: 16,
                    color: "rgba(255,255,255,0.6)",
                    transform: showDrawerLangDropdown
                      ? "rotate(180deg)"
                      : "rotate(0deg)",
                    transition: "0.2s",
                  }}
                />
              </ListItemButton>
            </ListItem>
            {showDrawerLangDropdown && (
              <>
                <Box
                  sx={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 4,
                  }}
                  onClick={() => setShowDrawerLangDropdown(false)}
                />
                <Box
                  sx={{
                    position: "absolute",
                    bottom: "100%",
                    left: 0,
                    right: 0,
                    mb: 0.5,
                    bgcolor: "#ffffff",
                    borderRadius: 2,
                    boxShadow: "0 -4px 20px rgba(0,0,0,0.2)",
                    zIndex: 10,
                    overflow: "hidden",
                  }}
                >
                  <MenuItem
                    onClick={() => handleLanguageChange("English", "en")}
                    sx={{ fontSize: "0.9rem", py: 1.2, px: 2 }}
                  >
                    🇬🇧 English
                  </MenuItem>
                  <MenuItem
                    onClick={() => handleLanguageChange("தமிழ்", "ta")}
                    sx={{ fontSize: "0.9rem", py: 1.2, px: 2 }}
                  >
                    🇮🇳 தமிழ்
                  </MenuItem>
                </Box>
              </>
            )}
          </Box>
        )}
        {isAuthenticated && (
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => {
                onLogout();
                setDrawerOpen(false);
              }}
              sx={{
                borderRadius: 2,
                py: 1,
                px: 1.5,
                bgcolor: "rgba(255,255,255,0.08)",
              }}
            >
              <ListItemIcon sx={{ minWidth: 36, color: "#fff" }}>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Logout"
                primaryTypographyProps={{
                  fontSize: "0.85rem",
                  color: "#fff",
                  fontWeight: 600,
                }}
              />
            </ListItemButton>
          </ListItem>
        )}
      </Box>
    </Box>
  );

  return (
    <>
      <AppBar
        position={isMobile ? "fixed" : "sticky"}
        className="custom-header"
        elevation={0}
        sx={{ zIndex: isMobile ? 1200 : theme.zIndex.appBar }}
      >
        <Toolbar className="header-toolbar">
          {/* Left: Hamburger + Logo */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 1, sm: 1.5 },
              flexShrink: 0,
            }}
          >
            {isMobile && (
              <IconButton
                color="inherit"
                onClick={() => setDrawerOpen(true)}
                edge="start"
                sx={{ color: "#fff" }}
                aria-label="Open menu"
              >
                <MenuIcon sx={{ fontSize: 24 }} />
              </IconButton>
            )}
            <Link
              to="/"
              style={{
                display: "flex",
                alignItems: "center",
                textDecoration: "none",
              }}
            >
              <img
                src="/TN.png"
                alt="Logo"
                style={{ height: "50px", objectFit: "contain" }}
              />
            </Link>
          </Box>

          {/* Center: Desktop Nav */}
          {!isMobile && (
            <Box
              className="header-nav"
              sx={{
                display: "flex",
                gap: 0.25,
                flex: 1,
                justifyContent: "center",
                overflow: "hidden",
                mx: 2,
              }}
            >
              {navItems.map((item, index) => {
                // RENDER DROPDOWN BUTTON
                if (item.children) {
                  const isDropdownActive = isPathActive(
                    item.children.map((c) => c.path),
                  );
                  return (
                    <Box
                      key={`dropdown-${index}`}
                      sx={{ display: "flex", alignItems: "center" }}
                    >
                      <Button
                        onClick={handleMasterMenuClick}
                        endIcon={
                          <ExpandMoreIcon sx={{ fontSize: 16, ml: 0.5 }} />
                        }
                        startIcon={item.icon}
                        sx={{
                          color: isDropdownActive ? "#ffc022" : "#fff",
                          fontSize: "1rem",
                          fontWeight: isDropdownActive ? 700 : 500,
                          textTransform: "none",
                          px: { md: 1.2, lg: 1.5 },
                          py: 0.5,
                          borderRadius: 1.5,
                          bgcolor: isDropdownActive
                            ? "rgba(255,193,7,0.1)"
                            : "transparent",
                          "&:hover": {
                            bgcolor: "rgba(255,255,255,0.1)",
                            color: "#ffc022",
                          },
                          "& .MuiButton-startIcon": {
                            mr: isTablet ? 0.3 : 0.5,
                            "& .MuiSvgIcon-root": {
                              fontSize: isTablet ? "16px" : "18px",
                            },
                          },
                        }}
                      >
                        {item.label}
                      </Button>
                      <Menu
                        anchorEl={masterMenuAnchor}
                        open={isMasterMenuOpen}
                        onClose={handleMasterMenuClose}
                        anchorOrigin={{
                          vertical: "bottom",
                          horizontal: "left",
                        }}
                        transformOrigin={{
                          vertical: "top",
                          horizontal: "left",
                        }}
                        PaperProps={{
                          sx: {
                            borderRadius: 2,
                            minWidth: 180,
                            mt: 1,
                            boxShadow: "0px 4px 12px rgba(0,0,0,0.15)",
                          },
                        }}
                      >
                        {item.children.map((child) => (
                          <MenuItem
                            key={child.path}
                            component={Link}
                            to={child.path}
                            onClick={handleMasterMenuClose}
                            sx={{
                              fontSize: "0.9rem",
                              color:
                                location.pathname === child.path
                                  ? "#4a8234"
                                  : "inherit",
                              fontWeight:
                                location.pathname === child.path ? 700 : 400,
                              bgcolor:
                                location.pathname === child.path
                                  ? "rgba(74, 130, 52, 0.1)"
                                  : "transparent",
                              "&:hover": { bgcolor: "rgba(74, 130, 52, 0.1)" },
                            }}
                          >
                            {child.label}
                          </MenuItem>
                        ))}
                      </Menu>
                    </Box>
                  );
                }

                // RENDER STANDARD BUTTON
                const isActive = location.pathname === item.path;
                return (
                  <Button
                    key={item.path || index}
                    component={Link}
                    to={item.path}
                    startIcon={item.icon}
                    sx={{
                      color: isActive ? "#ffc022" : "#fff",
                      fontSize: "1rem",
                      fontWeight: isActive ? 700 : 500,
                      textTransform: "none",
                      px: { md: 1.2, lg: 1.5 },
                      py: 0.5,
                      borderRadius: 1.5,
                      position: "relative",
                      minWidth: 0,
                      whiteSpace: "nowrap",
                      bgcolor: isActive ? "rgba(255,193,7,0.1)" : "transparent",
                      "&:hover": {
                        bgcolor: isActive
                          ? "rgba(255,193,7,0.15)"
                          : "rgba(255,255,255,0.1)",
                        color: "#ffc022",
                      },
                      "& .MuiButton-startIcon": {
                        mr: isTablet ? 0.3 : 0.5,
                        "& .MuiSvgIcon-root": {
                          fontSize: isTablet ? "16px" : "18px",
                        },
                      },
                      ...(isActive && {
                        "&::after": {
                          content: '""',
                          position: "absolute",
                          bottom: 2,
                          left: "20%",
                          right: "20%",
                          height: 2.5,
                          borderRadius: 2,
                          bgcolor: "#ffc022",
                        },
                      }),
                    }}
                  >
                    {isTablet
                      ? item.label.length > 12
                        ? item.label.slice(0, 10) + "…"
                        : item.label
                      : item.label}
                  </Button>
                );
              })}
            </Box>
          )}

          {/* Right: Actions */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 0.5, sm: 1 },
              flexShrink: 0,
            }}
          >
            {!isMobile && !isAuthenticated && (
              <>
                <Button
                  onClick={handleDesktopLangOpen}
                  startIcon={<LanguageIcon sx={{ fontSize: 18 }} />}
                  sx={{
                    color: "#fff",
                    fontSize: "1rem",
                    textTransform: "none",
                    px: 1.5,
                    borderRadius: 1.5,
                    "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
                    "& .MuiButton-startIcon": { mr: 0.5 },
                  }}
                >
                  {currentLang}
                  <ExpandMoreIcon sx={{ fontSize: 16, ml: 0.5 }} />
                </Button>
                <Menu
                  anchorEl={desktopLangAnchor}
                  open={Boolean(desktopLangAnchor)}
                  onClose={handleDesktopLangClose}
                  PaperProps={{
                    sx: { borderRadius: 2, minWidth: 150, mt: 0.5 },
                  }}
                >
                  <MenuItem
                    onClick={() => handleLanguageChange("English", "en")}
                    sx={{ fontSize: "0.85rem", py: 1 }}
                  >
                    🇬🇧 English
                  </MenuItem>
                  <MenuItem
                    onClick={() => handleLanguageChange("தமிழ்", "ta")}
                    sx={{ fontSize: "0.85rem", py: 1 }}
                  >
                    🇮🇳 தமிழ்
                  </MenuItem>
                </Menu>
              </>
            )}

            {isMobile && (
              <Box
                onClick={() => setDrawerOpen(true)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  cursor: "pointer",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    display: { xs: "none", sm: "block" },
                  }}
                >
                  {authData?.username || "User"}
                </Typography>
                <Avatar
                  sx={{
                    width: 34,
                    height: 34,
                    bgcolor: "#ffc022",
                    color: "#333",
                    fontWeight: 700,
                    fontSize: "0.8rem",
                    border: "2px solid rgba(255,255,255,0.5)",
                  }}
                >
                  {getInitials(authData?.username)}
                </Avatar>
              </Box>
            )}

            {!isMobile && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ textAlign: "right" }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: "0.85rem",
                      lineHeight: 1.2,
                    }}
                  >
                    {authData?.username || "User"}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "rgba(255,255,255,0.65)",
                      fontSize: "0.65rem",
                    }}
                  >
                    {getRoleLabel(authData?.role)}
                  </Typography>
                </Box>
                <Avatar
                  sx={{
                    width: 40,
                    height: 40,
                    bgcolor: "#ffc022",
                    color: "#333",
                    fontWeight: 700,
                    fontSize: "0.9rem",
                    border: "2px solid rgba(255,255,255,0.3)",
                  }}
                >
                  {getInitials(authData?.username)}
                </Avatar>
              </Box>
            )}

            {!isMobile && isAuthenticated && (
              <Tooltip title="Logout" arrow>
                <IconButton onClick={onLogout} sx={{ color: "#fff" }}>
                  <LogoutIcon sx={{ fontSize: 23 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {isMobile && <Toolbar />}

      {/* ===== MOBILE DRAWER ===== */}
      <Drawer
        variant="temporary"
        anchor="left"
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setShowDrawerLangDropdown(false);
        }}
        sx={{
          display: { md: "none" },
          "& .MuiModal-root": { zIndex: 1400 },
          "& .MuiDrawer-paper": {
            width: DrawerWidth,
            maxWidth: "85vw",
            boxShadow: "8px 0 32px rgba(0,0,0,0.5)",
          },
          "& .MuiBackdrop-root": { backgroundColor: "rgba(0,0,0,0.6)" },
        }}
        ModalProps={{ keepMounted: true, style: { zIndex: 1400 } }}
        transitionDuration={{ enter: 300, exit: 250 }}
      >
        <DrawerContent />
      </Drawer>
    </>
  );
};

export default Header;
